import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Pencil, KeyRound, Upload, Truck } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { SERVICE, DRIVER_STATUS, PAY_STATUS, NOTIF_TYPES, PUSH_STATUS, DOC_STATUS, fmtDate, fmtDateTime } from "@/lib/util";
import { Loader, Pill, Money, Row, Section, Field, FileButton, LocLink, VehNo, payColor, docColor, tripColor } from "@/components/common";
import Modal from "@/components/Modal";
import DocUploadModal from "@/components/DocUploadModal";
import FeeManager from "@/components/FeeManager";
import { Table, Td } from "@/pages/admin/ui";
import { WorkTypes } from "@/pages/admin/Drivers";
import { TRIP_STATUS, fmtChanges, NATIONALITIES } from "@/lib/util";

const TABS = [["info", "البيانات"], ["trips", "النقلات"], ["invoices", "الفواتير"], ["violations", "المخالفات والإشعارات"], ["fees", "الرسوم والدفعات"], ["docs", "المستندات"], ["audit", "سجل النشاط"]];
const FIELDS = [["full_name", "الاسم الكامل"], ["phone", "الهاتف"], ["civil_id", "الرقم المدني"], ["nationality", "الجنسية"], ["birth_date", "تاريخ الميلاد"],
  ["residency_no", "رقم الإقامة"], ["license_no", "رقم رخصة القيادة"], ["address", "العنوان"], ["hire_date", "تاريخ التعيين"], ["notes", "ملاحظات إدارية"]];

function EditModal({ d, open, onClose, onSaved }) {
  const [f, setF] = useState({});
  useEffect(() => { if (open) setF({ ...Object.fromEntries(FIELDS.map(([k]) => [k, d[k] || ""])), work_types: d.work_types, status: d.status }); }, [open, d]);
  const save = async () => {
    try { await api.patch(`/drivers/${d.id}`, f); toast.success("تم حفظ التعديل"); onSaved(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Modal open={open} onClose={onClose} title="تعديل ملف السائق" testid="edit-driver-modal" wide>
      <div className="grid sm:grid-cols-2 gap-3">
        {FIELDS.map(([k, l]) => <Field key={k} label={l}>{k === "nationality" ? (
          <select data-testid="ed-nationality" className="field field-sm" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })}>
            <option value="">اختر الجنسية</option>{[...new Set([...NATIONALITIES, ...(f[k] ? [f[k]] : [])])].map((n) => <option key={n} value={n}>{n}</option>)}</select>
        ) : <input data-testid={`ed-${k}`} className="field field-sm" value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} />}</Field>)}
      </div>
      <Field label="نوع العمل"><WorkTypes value={f.work_types || []} onChange={(v) => setF({ ...f, work_types: v })} /></Field>
      <Field label="حالة الحساب"><select data-testid="ed-status" className="field" value={f.status} onChange={(e) => setF({ ...f, status: e.target.value })}>
        {Object.entries(DRIVER_STATUS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
      <button data-testid="ed-save" onClick={save} className="btn btn-lg btn-blue w-full">حفظ</button>
    </Modal>
  );
}

export default function DriverProfile() {
  const { id } = useParams();
  const { can } = useAuth();
  const [p, setP] = useState(null);
  const [tab, setTab] = useState("info");
  const [modal, setModal] = useState("");
  const [doc, setDoc] = useState(null);
  const [pw, setPw] = useState("");
  const load = useCallback(() => api.get(`/drivers/${id}`).then((r) => setP(r.data)).catch((e) => { toast.error(errMsg(e)); setP(false); }), [id]);
  useEffect(() => { load(); }, [load]);
  if (p === null) return <Loader />;
  if (!p) return <p>السائق غير موجود</p>;
  const d = p.driver;
  const resetPw = async () => {
    try { await api.post(`/drivers/${id}/reset-password`, { password: pw }); toast.success("تم تعيين كلمة المرور"); setModal(""); setPw(""); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <div data-testid="driver-profile">
      <Link to="/admin/drivers" className="btn btn-sm btn-ghost mb-4"><ArrowRight className="w-4 h-4" />ملفات السائقين</Link>
      <div className="card p-6 mb-5 bg-brand text-white border-0">
        <div className="flex flex-wrap items-center justify-between gap-4">
          <div>
            <span data-testid="profile-serial" className="ltr bg-slate-800 text-amber-400 font-black px-3 py-1 rounded-full border border-amber-500/40">{d.serial}</span>
            <h1 className="text-3xl font-black mt-3" data-testid="profile-name">{d.full_name}</h1>
            <div className="text-slate-300 mt-1"><span className="ltr">{d.phone}</span> • {d.work_types.map((w) => SERVICE[w]).join(" + ")} • المركبة <VehNo adm={d.vehicle_admin_number} plate={d.vehicle_plate} testid="profile-vehicle" /></div>
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <Pill color={d.status === "active" ? "green" : "red"} testid="profile-status">{DRIVER_STATUS[d.status]}</Pill>
            {can("drivers_manage") && <>
              <button data-testid="edit-driver-btn" onClick={() => setModal("edit")} className="btn btn-sm bg-white text-brand"><Pencil className="w-4 h-4" />تعديل</button>
              <button data-testid="reset-pw-btn" onClick={() => setModal("pw")} className="btn btn-sm bg-white/10 text-white"><KeyRound className="w-4 h-4" />كلمة المرور</button></>}
          </div>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-2 mb-4">
        {TABS.map(([k, l]) => <button key={k} data-testid={`profile-tab-${k}`} onClick={() => setTab(k)}
          className={`h-11 px-4 rounded-xl font-bold whitespace-nowrap ${tab === k ? "bg-brand text-white" : "bg-white border"}`}>{l}</button>)}
      </div>

      {tab === "info" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Section title="البيانات الشخصية والإدارية">
            {FIELDS.map(([k, l]) => <Row key={k} label={l}>{d[k] || "—"}</Row>)}
            <Row label="تاريخ إنشاء الحساب">{fmtDateTime(d.created_at)}</Row>
            <Row label="آخر دخول">{fmtDateTime(d.last_login_at)}</Row>
            <Row label="لغة التطبيق" testid="driver-lang">{{ ar: "العربية", en: "الإنجليزية (English)", hi: "الهندية (हिन्दी)", bn: "البنغالية (বাংলা)" }[d.lang] || "العربية"}</Row>
          </Section>
          <Section title={<span className="flex items-center gap-2"><Truck className="w-5 h-5" />سجل المركبات</span>}>
            {p.assignments.length === 0 ? <p className="text-slate-500">لم يرتبط بأي مركبة. <Link data-testid="link-vehicle-btn" to="/admin/vehicles" className="text-blue-700 font-bold">ربط مركبة من صفحة المركبات</Link></p> : (
              <div className="space-y-2" data-testid="assignment-history">{p.assignments.map((a) => (
                <div key={a.id} className={`rounded-xl p-3 ${a.end_at ? "bg-slate-50" : "bg-blue-50"}`}>
                  <VehNo adm={a.admin_number} plate={a.plate} /> {!a.end_at && <Pill color="blue">حالياً</Pill>}
                  <div className="text-sm text-slate-600">من {fmtDateTime(a.start_at)} {a.end_at ? `إلى ${fmtDateTime(a.end_at)}` : ""}</div>
                </div>))}</div>)}
          </Section>
        </div>
      )}
      {tab === "trips" && (
        <Table head={["رقم النقلة", "التاريخ", "الخدمة", "المسار", "السعر", "الدفع", "الحالة"]} testid="profile-trips">
          {p.trips.map((t) => <tr key={t.id}><Td><Link to={`/admin/trips/${t.id}`} className="ltr text-blue-700 font-bold">{t.number}</Link></Td><Td>{fmtDateTime(t.created_at)}</Td>
            <Td>{SERVICE[t.service_type]}</Td><Td><bdi>{t.pickup}</bdi> ← <bdi>{t.dropoff}</bdi></Td><Td><Money fils={t.price_fils} /></Td>
            <Td><Pill color={payColor(t.payment_status)}>{PAY_STATUS[t.payment_status]}</Pill></Td><Td><Pill color={tripColor(t.status)}>{TRIP_STATUS[t.status]}</Pill></Td></tr>)}
        </Table>
      )}
      {tab === "invoices" && (
        <Table head={["رقم الفاتورة", "التاريخ", "المبلغ", "المحصل", "الحالة"]} testid="profile-invoices">
          {p.invoices.map((i) => <tr key={i.id}><Td><Link to={`/admin/invoices/${i.id}`} className="ltr text-blue-700 font-bold">{i.number}</Link></Td><Td>{fmtDateTime(i.issued_at)}</Td>
            <Td><Money fils={i.total_fils} /></Td><Td><Money fils={i.paid_fils} /></Td><Td><Pill color={payColor(i.payment_status)}>{PAY_STATUS[i.payment_status]}</Pill></Td></tr>)}
        </Table>
      )}
      {tab === "violations" && (
        <div className="grid lg:grid-cols-2 gap-5">
          <Section title="المخالفات">
            {p.violations.length === 0 && <p className="text-slate-500">لا توجد مخالفات</p>}
            {p.violations.map((v) => <div key={v.id} className="border-b py-3"><div className="flex justify-between"><b>{v.violation_type}</b><Money fils={v.amount_fils} className="text-red-600" /></div>
              <div className="text-sm text-slate-500">{fmtDate(v.date)} • <VehNo adm={v.admin_number} plate={v.plate} /> • {v.seen_at ? `اطلع ${fmtDateTime(v.seen_at)}` : v.viewed_at ? `شاهدها ${fmtDateTime(v.viewed_at)} دون الضغط على «اطلعت»` : "لم يشاهدها"}</div>
              {v.replies.map((r, i) => <div key={i} className="text-sm bg-slate-50 rounded p-2 mt-1">رد السائق: {r.text}{r.text_ar && <div className="text-blue-800" data-testid="reply-ar">الترجمة: {r.text_ar}</div>}</div>)}
              <FileButton fileId={v.file_id} label="المرفق" className="btn btn-sm btn-ghost mt-1" testid={`pv-file-${v.id}`} /></div>)}
          </Section>
          <Section title="الإشعارات">
            {p.notifications.map((r) => <div key={r.id} className="border-b py-3"><b>{r.notification?.title}</b>
              <div className="text-sm text-slate-500">{NOTIF_TYPES[r.notification?.type]} • {fmtDateTime(r.created_at)}</div>
              <div className="flex gap-2 mt-1 flex-wrap"><Pill>{PUSH_STATUS[r.push_status]}</Pill>
                <Pill color={r.delivered_at ? "blue" : "slate"}>{r.delivered_at ? "وصل للتطبيق" : "لم يصل بعد"}</Pill>
                <Pill color={r.read_at ? "green" : "red"}>{r.read_at ? `قُرئ ${fmtDateTime(r.read_at)}` : "لم يُقرأ"}</Pill></div></div>)}
          </Section>
        </div>
      )}
      {tab === "fees" && can("fees_manage") && <Section title="الرسوم الإدارية والدفعات"><FeeManager driver={d} s={p.statement} reload={load} /></Section>}
      {tab === "docs" && (
        <Section title="المستندات" action={can("documents_manage") && <button data-testid="upload-doc-btn" onClick={() => { setDoc(null); setModal("doc"); }} className="btn btn-sm btn-green"><Upload className="w-4 h-4" />رفع مستند</button>}>
          <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-3">
            {p.documents.map((x) => <div key={x.id} className="rounded-2xl border p-4" data-testid={`profile-doc-${x.doc_type}`}>
              <div className="flex justify-between gap-2"><b>{x.name}</b><Pill color={docColor(x.status)}>{DOC_STATUS[x.status]}</Pill></div>
              <div className="text-sm text-slate-600 mt-1">رقم: {x.number || "—"} • إصدار {fmtDate(x.issue_date)} • انتهاء {fmtDate(x.expiry_date)}</div>
              <div className="text-xs text-slate-400">رُفع {fmtDateTime(x.uploaded_at)} • آخر تحديث {fmtDateTime(x.updated_at)} {x.visible_to_driver ? "" : "• مخفي عن السائق"}</div>
              <div className="flex gap-2 mt-2"><FileButton fileId={x.file_id} label="عرض" className="btn btn-sm btn-ghost" testid={`doc-open-${x.id}`} />
                {can("documents_manage") && <button onClick={() => { setDoc(x); setModal("doc"); }} className="btn btn-sm btn-ghost">تحديث</button>}</div>
            </div>)}
            {p.documents.length === 0 && <p className="text-slate-500">لا توجد مستندات</p>}
          </div>
        </Section>
      )}
      {tab === "audit" && (
        <Table head={["الوقت", "الإجراء", "بواسطة", "الموقع", "التغيير"]} testid="profile-audit">
          {p.audit.map((a) => <tr key={a.id}><Td className="whitespace-nowrap">{fmtDateTime(a.at)}</Td><Td>{a.action}</Td><Td>{a.user_name}</Td><Td>{a.location ? <LocLink loc={a.location} /> : "—"}</Td>
            <Td className="text-xs max-w-md">{fmtChanges(a.after)}</Td></tr>)}
        </Table>
      )}
      <EditModal d={d} open={modal === "edit"} onClose={() => setModal("")} onSaved={load} />
      <DocUploadModal open={modal === "doc"} onClose={() => setModal("")} driverId={d.id} doc={doc} onSaved={load} />
      <Modal open={modal === "pw"} onClose={() => setModal("")} title="تعيين كلمة مرور جديدة" testid="reset-pw-modal">
        <input data-testid="reset-pw-input" className="field" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="6 أحرف على الأقل" />
        <button data-testid="reset-pw-save" onClick={resetPw} className="btn btn-lg btn-dark w-full">حفظ</button>
      </Modal>
    </div>
  );
}
